import { Shield } from 'lucide-react';
import type { MouseEvent } from 'react';
import { readConsentBridge, usePerspectifyConsentAvailable } from '../../hooks/usePerspectifyConsent';

export const PRIVACY_CHOICES_LABEL = 'Privacy choices';

const VARIANT_CLASS = {
  // Same typography and spacing as the sidebar's nav links; a full 44px target in the
  // touch-oriented mobile drawer, the nav links' own height on the desktop sidebar.
  sidebar:
    'flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-slate-400 transition hover:bg-slate-900/50 hover:text-slate-200 lg:min-h-0',
  link: 'inline-flex min-h-[44px] items-center gap-1.5 rounded text-xs underline-offset-4 transition hover:underline',
} as const;

/**
 * Opens the app's own visitor-consent preferences, the same dialog the SDK's floating
 * launcher would open. Renders nothing until the SDK bridge is available.
 *
 * Distinct from the "Consent" navigation item, which administers a project's policy.
 */
export function PrivacyChoicesButton({
  variant,
  inheritColor = false,
}: {
  variant: keyof typeof VARIANT_CLASS;
  /** For pages with their own light/dark palette, such as public status pages. */
  inheritColor?: boolean;
}) {
  const available = usePerspectifyConsentAvailable();
  if (!available) return null;

  const open = (event: MouseEvent<HTMLButtonElement>) => {
    // The SDK returns focus to whatever was focused when the dialog opened. Safari does
    // not focus a button on click, so make sure it is this one.
    event.currentTarget.focus();
    readConsentBridge()?.openPreferences();
  };

  return (
    <button
      type="button"
      onClick={open}
      aria-haspopup="dialog"
      className={`${VARIANT_CLASS[variant]}${
        variant === 'link' && !inheritColor ? ' text-slate-400 hover:text-slate-200' : ''
      }`}
    >
      <Shield className={variant === 'sidebar' ? 'h-4 w-4 shrink-0' : 'h-3.5 w-3.5 shrink-0'} aria-hidden="true" />
      <span className={variant === 'sidebar' ? 'truncate' : undefined}>{PRIVACY_CHOICES_LABEL}</span>
    </button>
  );
}
